import { skipToken } from "@reduxjs/toolkit/query/react";
import { useGetQuestionnaireSectionsQuery } from "../../api/questionnaire";
import { useGetCurrentUserQuery } from "../../api/users";
import { QuestionnaireSectionStatus } from "../../../../api/src/types";
import styled from "@emotion/styled";
import Loading from "../../components/Loading";
import Error from "../../components/Error";
import { COLOR_APP_TEXT } from "../../components/theme";
import { ParagraphSmall } from "../../components/typography/Paragraph";
import { Link } from "react-router";
import NavCheckbox from "../../components/icons/NavCheckbox";
import { IconSize } from "../../components/theme";

const StyledSectionList = styled.div`
  display: flex;
  flex-direction: column;
  color: ${COLOR_APP_TEXT};
`;

const StyledLink = styled(Link)`
  margin-top: 12px;
  text-decoration: underline;
  color: ${COLOR_APP_TEXT};
  &:hover {
    color: ${COLOR_APP_TEXT};
  }
`;

const StyledCheckboxContainerItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionnaireFinalSectionValidation = ({
  setValidationMessagePopupOpen,
}: {
  setValidationMessagePopupOpen: (open: boolean) => void;
}) => {
  const { data: user } = useGetCurrentUserQuery();
  const {
    data: sections,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
  } = useGetQuestionnaireSectionsQuery(
    user?.id ? { id: user.id, includeStatus: true } : skipToken,
  );

  const inProgressSections = sections?.filter(
    (section) => section.status !== QuestionnaireSectionStatus.COMPLETED,
  );

  if (isSectionsLoading) {
    return <Loading />;
  }

  if (isSectionsError) {
    return (
      <Error>
        Unable to load questionnaire sections. Please refresh the page and try
        again.
      </Error>
    );
  }

  return (
    <>
      <ParagraphSmall color={COLOR_APP_TEXT}>
        To continue, please complete the following sections:
      </ParagraphSmall>
      <StyledSectionList>
        {inProgressSections?.map((section) => (
          <StyledLink
            key={section.id}
            to={`/questionnaire/${section.label.toLowerCase().replace(/ /g, "-")}`}
            onClick={() => {
              setValidationMessagePopupOpen(false);
            }}
          >
            <ParagraphSmall color={COLOR_APP_TEXT}>
              {section.label}
            </ParagraphSmall>
          </StyledLink>
        ))}
        <ParagraphSmall
          marginTop="32px"
          marginBottom="32px"
          color={COLOR_APP_TEXT}
        >
          Note: Check that a section is completed by looking at the left-side
          menu.
        </ParagraphSmall>

        <StyledCheckboxContainer>
          <StyledCheckboxContainerItem>
            <NavCheckbox isChecked={true} size={IconSize.M} />
            <ParagraphSmall color={COLOR_APP_TEXT}>
              =&nbsp;&nbsp;Completed
            </ParagraphSmall>
          </StyledCheckboxContainerItem>
          <StyledCheckboxContainerItem>
            <NavCheckbox isChecked={false} size={IconSize.M} />
            <ParagraphSmall color={COLOR_APP_TEXT}>
              =&nbsp;&nbsp;In Progress
            </ParagraphSmall>
          </StyledCheckboxContainerItem>
        </StyledCheckboxContainer>
      </StyledSectionList>
    </>
  );
};

export default QuestionnaireFinalSectionValidation;
